// FIXME no-param-reassign
/* eslint-disable no-param-reassign */

import * as URL from './url';

type Rule = {
  error?: boolean | string;
  pattern: string;
  // eslint-disable-next-line camelcase
  match_operation: null | 'simple' | 'exact' | 'contains' | 'starts_with' | 'regex' | 'ends_with';
  component: 'attribute' | 'trigger' | 'url';
};

export type Summary = {
  rules: Rule[];
  attributeRules: Rule[];
  triggerRules: Rule[];
  urlRules: Rule[];
};

/** ***********************
 * PRIVATE VARIABLES / FUNCTIONS
 ************************ */
const validateRule = {
  attribute: (rule: Rule) => {
    if (/[^\w. -]/gi.test(rule.pattern)) {
      rule.error =
        'Invalid characters. Please use any of the following: a-z, A-Z, numbers, spaces, _ and -.';
    }
  },
  trigger: (rule: Rule) => {
    if (/[^\w -:/]/gi.test(rule.pattern)) {
      rule.error =
        'Invalid characters. Please use any of the following: a-z, A-Z, 0-9, spaces, _, -, : and /.';
    }
  },
  url: (rule: Rule, normalize: boolean) => {
    switch (rule.match_operation) {
      case 'simple':
      case 'exact':
        if (URL.validateHTTPSUrl(rule.pattern)) {
          if (normalize) {
            rule.pattern = URL.normalize(rule.pattern);
          }
        } else {
          rule.error = 'Invalid URL format: e.g. https://www.example.com/page/.';
        }
        break;

      case 'starts_with':
        if (URL.validateHTTPSUrl(rule.pattern)) {
          if (normalize) {
            rule.pattern = URL.normalize(rule.pattern);
          }
        } else {
          rule.error = 'Invalid URL format: e.g. https://www.example.com/page/.';
        }
        break;

      case 'regex':
        try {
          // nosemgrep
          RegExp(rule.pattern);
        } catch (e) {
          rule.error = 'Invalid regular expression.';
        }

        break;

      case 'ends_with':
      case 'contains':
        // Do nothing.
        break;

      default:
        rule.error = 'Unhandled match operation.';
        throw new Error(`Unhandled match operation "${rule.match_operation}"`);
    }
  },
};

/** ***********************
 * PUBLIC FUNCTIONS
 ************************ */

/**
 * Validate a targeting rule and return it in its final correct, normalized format. This function will update
 * the object 'rule' itself.
 *
 * @param rule - Consists of at least: component, match_operation, negate and pattern.
 * @param {Boolean} normalize - Normalizes the pattern value. When true this performs a mutation on the rule object.
 */
export const validateAndNormalize = (rule: Rule, normalize = true): void => {
  rule.error = false;

  // Supported targeting rule components.
  if (!['attribute', 'trigger', 'url'].includes(rule.component)) {
    rule.error = 'Unhandled match operation.';
    throw new Error(`Unhandled targeting rule component "${rule.component}"`);
  }

  // Exit early if the rule pattern is empty.
  if (!rule.pattern || !rule.pattern.trim()) {
    rule.error = 'This field cannot be left empty.';
    return;
  }

  // Rule-specific validation.
  validateRule[rule.component](rule, normalize);

  // Delete the error key if it's false.
  // Note: We don't use a private variable as this would still be needed when the user corrects the error.
  if (!rule.error) {
    delete rule.error;
  }
};

/**
 * Compile a summary of the targeting rules currently active.
 *
 * @param targetingRules Current snapshot targeting rules.
 */
export const summary = (targetingRules: Rule[]): Summary => {
  targetingRules = targetingRules || [];

  return {
    rules: targetingRules,
    attributeRules: targetingRules.filter((r: Rule) => {
      return r.component === 'attribute';
    }),
    triggerRules: targetingRules.filter((r: Rule) => {
      return r.component === 'trigger';
    }),
    urlRules: targetingRules.filter((r: Rule) => {
      return r.component === 'url';
    }),
  };
};

// A list of pre-made rule objects used by controllers.
export const ruleObject = {
  attribute: {
    empty: {
      component: 'attribute',
      match_operation: null,
      name: null,
      negate: false,
      pattern: null,
      rule_type: null,
    },
  },
  url: {
    empty: {
      component: 'url',
      match_operation: 'simple',
      negate: false,
      pattern: '',
    },
    allPages: {
      component: 'url',
      match_operation: 'contains',
      negate: false,
      pattern: '/',
    },
  },
};

// Helper object: URL match targeting text.
export const urlMatchOperations = {
  simple: {
    label: 'Simple URL match',
    placeholder: 'e.g. https://www.example.com/',
    tooltip:
      'Target a URL regardless of any # and ? values it may include.<br />' +
      '<strong>Click for more info</strong>',
    helpUrl:
      'https://help.hotjar.com/hc/en-us/articles/115014943188-Page-Targeting-for-Feedback-Tools',
  },
  exact: {
    label: 'Exact URL match',
    placeholder: 'e.g. https://www.example.com/?product=X',
    tooltip: 'Target a URL exactly as specified.<br /><strong>Click for more info</strong>',
    helpUrl:
      'https://help.hotjar.com/hc/en-us/articles/115014943188-Page-Targeting-for-Feedback-Tools',
  },
  starts_with: {
    label: 'URL starts with',
    placeholder: 'e.g. https://www.example.com/page/',
    tooltip:
      'Target all pages which URLs start in a certain way.<br />' +
      '<strong>Click for more info</strong>',
    helpUrl:
      'https://help.hotjar.com/hc/en-us/articles/115014943188-Page-Targeting-for-Feedback-Tools',
  },
  ends_with: {
    label: 'URL ends with',
    placeholder: 'e.g. /detail',
    tooltip:
      'Target all pages which URLs end in a certain way.<br />' +
      '<strong>Click for more info</strong>',
    helpUrl:
      'https://help.hotjar.com/hc/en-us/articles/115014943188-Page-Targeting-for-Feedback-Tools',
  },
  contains: {
    label: 'URL contains',
    placeholder: 'e.g. register/',
    tooltip:
      'Target all pages which URLs contain something specific.<br />' +
      '<strong>Click for more info</strong>',
    helpUrl:
      'https://help.hotjar.com/hc/en-us/articles/115014943188-Page-Targeting-for-Feedback-Tools',
  },
  regex: {
    label: 'URL regular expression',
    placeholder: 'e.g. \\/pages\\/[^\\/]+',
    tooltip:
      'Target all pages which URL matches a regular expression.<br />' +
      '<strong>Click for more info</strong>',
    helpUrl:
      'https://help.hotjar.com/hc/en-us/articles/115012727628-Using-Regular-Expressions-to-Target-Multiple-Pages',
  },
};

// Helper object: JS Triggers Text.
export const triggerMatchOperation = {
  label: 'Event',
  placeholder: 'e.g. saw_homepage_variation_b',
  tooltip:
    'Target users performing a specific action by tracking an event with our Events API.<br />' +
    '<strong>Click to learn more</strong>.',
  helpUrl: 'https://help.hotjar.com/hc/en-us/articles/115011624487',
};
